import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Category,
  convertCategoryEntityListToCategoryList,
} from "../../../domain/model/Category";
import { GetAllCategoriesUseCase } from "../../../domain/usecase/category/getAll/GetAllUseCase";
import { State } from "../../../domain/model/ResponseState";
import { Difficulty } from "../../../domain/model/Question";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { Game, GameData, TeamTurns } from "../../../domain/model/Game";
import { Player } from "../../../domain/model/User";
import { CreateGameUseCase } from "../../../domain/usecase/game/create/UseCase";

export function NewGameViewModel(
  player: Player,
  getAllCategoriesUseCase: GetAllCategoriesUseCase,
  createGameUseCase: CreateGameUseCase,
) {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(NewGamePage.CHOOSE_NAME);
  const [activeStep, setActiveStep] = useState(0);

  const [firstTeamName, setFirstTeamName] = useState("");
  const [secondTeamName, setSecondTeamName] = useState("");

  const [loading, setLoading] = useState(false);
  const [gameStartingLoading, setGameStartingLoading] = useState(false);

  const totalPages = Object.values(NewGamePage).length;

  const [categories, setCategories] = useState<Array<Category>>([]);
  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >();

  const navigate = useNavigate();

  useEffect(() => {
    getCategories();
  }, []);

  async function getCategories() {
    setLoading(true);
    const response = await getAllCategoriesUseCase.invoke();
    setLoading(false);
    switch (response.responseState) {
      case State.Success:
        console.log(response.data);
        if (response.data) {
          setCategories(convertCategoryEntityListToCategoryList(response.data));
        }
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function onCategoryClicked(category: Category) {
    const index = categories.findIndex((c) => c.id == category.id);
    if (index == -1) return;

    if (!category.isSelected) {
      if (categories.filter((c) => c.isSelected).length > 5) {
        showSnackbar(t("you_can_only_choose_maximum_6_categories"));
        return;
      }
    }

    const categoriesList = [...categories];
    categoriesList[index].isSelected = !category.isSelected;
    setCategories(categoriesList);
  }

  const handleOnFirstTeamNameChanged = (txt: string) => {
    setFirstTeamName(txt);
  };
  const handleOnSecondTeamNameChanged = (txt: string) => {
    setSecondTeamName(txt);
  };

  const handleNext = () => {
    if (currentPage == NewGamePage.CHOOSE_NAME) {
      if (!firstTeamName || !secondTeamName) {
        showSnackbar(t("choose_teams_name"));
        return;
      }
      setCurrentPage(NewGamePage.CHOOSE_CATEGORIES);
      setActiveStep(1);
      return;
    }
    setCurrentPage(Object.values(NewGamePage)[activeStep + 1]);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (currentPage == NewGamePage.CHOOSE_CATEGORIES) {
      setCurrentPage(NewGamePage.CHOOSE_NAME);
      setActiveStep(0);
      return;
    }
    setCurrentPage(Object.values(NewGamePage)[activeStep - 1]);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const startGame = async () => {
    if (categories.filter((c) => c.isSelected).length != 6) {
      showSnackbar(t("should_choose_6_categories_before_satrting_game"));
      return;
    }

    const resultGame = await addToUserGames();
    if (!resultGame) return;

    const gameData: GameData = {
      firstTeamName,
      secondTeamName,
      categories: categories.filter((c) => c.isSelected),
      isLoading: false,
      firstTeamScore: 0,
      secondTeamScore: 0,
      currentTeamTurn: TeamTurns.teamA,
      resultGame,
    };

    navigate(ROUTE_ENDPOINTS.GAME, { state: gameData });
  };

  async function addToUserGames(): Promise<Game | undefined> {
    setGameStartingLoading(true);
    const response = await createGameUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        return response.data;
      case State.Fail:
      case State.Error:
        setGameStartingLoading(false);
        showSnackbar(response.error?.message);
        break;
    }
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" | "warning" = "error",
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    currentPage,
    activeStep,
    totalPages,
    handleNext,
    handleBack,
    firstTeamName,
    secondTeamName,
    handleOnFirstTeamNameChanged,
    handleOnSecondTeamNameChanged,
    categories,
    setSelectedCategory,
    selectedCategory,
    loading,
    onCategoryClicked,

    startGame,
    gameStartingLoading,
  };
}

export enum NewGamePage {
  CHOOSE_NAME = "ChooseName",
  CHOOSE_CATEGORIES = "ChooseCategories",
}
